import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import http, { formatApiErrorDetail } from "@/api";
import { useAuth } from "@/auth";
import Mariposa from "@/components/Mariposa";

export default function AcceptInvitation() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get(`/invitations/${token}`)
      .then((r) => { setInv(r.data); })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await http.post(`/invitations/${token}/accept`, { name, password });
      // Token is now stored in HttpOnly cookie by backend
      await refresh();
      nav("/mi-evaluacion");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  if (loading) return <div className="px-6 py-20 text-center font-hand text-2xl text-[var(--lala-blue)]">Validando invitación…</div>;

  if (err && !inv) {
    return (
      <div className="paper min-h-screen flex items-center justify-center px-4">
        <div className="sketch-border bg-[var(--cream-soft)] p-8 max-w-md text-center">
          <Mariposa size={120} className="mx-auto opacity-50" />
          <h1 className="font-display text-3xl mt-3">Invitación no válida</h1>
          <p className="text-[var(--ink-soft)] mt-2">{err}</p>
          <Link to="/login" className="btn-ghost mt-4 inline-block" data-testid="back-to-login">Ir al login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="paper min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 items-center">
        <div className="hidden md:block">
          <Mariposa size={200} className="mb-4 float-y" />
          <div className="font-hand text-2xl text-[var(--lala-blue)]">bienvenido a la familia</div>
          <h1 className="font-display text-5xl mt-2 leading-tight">Tu invitación a <span className="hand-underline">Lala Elite</span></h1>
          <p className="text-[var(--ink-soft)] mt-4 max-w-md">
            Grupo Lala te invita a unirte al programa de evaluación 360° de proveedores.
            Crea tu cuenta y empieza a consultar tu scorecard, sello digital y plan de mejora.
          </p>
        </div>

        <form onSubmit={submit} className="sketch-border bg-[var(--cream-soft)] p-8" data-testid="accept-form">
          <div className="font-hand text-lg text-[var(--lala-blue)]">invitación para</div>
          <h2 className="font-display text-3xl" data-testid="accept-supplier-name">{inv.supplier_name}</h2>
          {inv.contact_email && (
            <p className="text-sm text-[var(--ink-soft)] mt-1">
              Tu cuenta se creará para <strong>{inv.contact_email}</strong>
            </p>
          )}
          <p className="text-xs text-[var(--ink-soft)] mt-1">Válida hasta {new Date(inv.expires_at).toLocaleDateString("es-MX")}</p>

          <label className="block mt-6 text-xs font-semibold uppercase tracking-wider text-[var(--ink-soft)]">Tu nombre completo</label>
          <input data-testid="accept-name" required value={name} onChange={(e) => setName(e.target.value)} className="input-field mt-1" placeholder="Carlos Pérez" />

          <label className="block mt-4 text-xs font-semibold uppercase tracking-wider text-[var(--ink-soft)]">Contraseña (mínimo 8 caracteres)</label>
          <input data-testid="accept-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="input-field mt-1" placeholder="••••••••" />

          {err && <div data-testid="accept-error" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}

          <button type="submit" data-testid="accept-submit" disabled={busy} className="btn-primary w-full mt-6 justify-center">
            {busy ? "Creando cuenta…" : "Crear mi cuenta y entrar"}
          </button>
        </form>
      </div>
    </div>
  );
}
